import React, {FC, useRef, useState} from 'react';
import cn from 'classnames';
import Image from 'next/image';

import {NON_ALPHA_NUMERIC_REGEXP} from '@zoefin/constants';
import {
  Button,
  Checkbox,
  InputMobile,
  InputString,
} from '@zoefin/design-system';
import {useMountTransition} from '@zoefin/hooks';
import {logger} from '@zoefin/logger';
import {request} from '@zoefin/request';
import {defaultMobileNumberValidator} from '@zoefin/schemes';

import type {FhsLeadPayload} from '@/modules/FinancialHealthScore/types';
import {
  getInvestableAssetsBucket,
  isEmailValid,
} from '@/modules/FinancialHealthScore/utils';

import {
  LEAD_INGEST_URL,
  PRIVACY_EMAIL,
  PRIVACY_POLICY_URL,
  TRANSITION_MS,
} from '../../constants';

import {SubmissionState} from './types';

import styles from './styles.module.scss';

interface LockedOverlayProps {
  isOpen: boolean;
  onUnlock: () => void;
  fullName: string;
  leadPayload: FhsLeadPayload;
}

const LockedOverlay: FC<LockedOverlayProps> = ({
  isOpen,
  onUnlock,
  fullName,
  leadPayload,
}) => {
  const {shouldMount, animationState} = useMountTransition(
    isOpen,
    TRANSITION_MS,
  );
  const [email, setEmail] = useState('');
  const [talkToAdvisor, setTalkToAdvisor] = useState(false);
  const [phone, setPhone] = useState('');
  const [submission, setSubmission] = useState<SubmissionState>({
    isSubmitting: false,
    error: null,
  });
  const isSubmittingRef = useRef(false);

  if (!shouldMount) return null;

  const cleanPhone = phone.replace(NON_ALPHA_NUMERIC_REGEXP, '');
  const isPhoneValid =
    defaultMobileNumberValidator('Phone number').safeParse(phone).success;
  const canSubmit =
    isEmailValid(email) &&
    (!talkToAdvisor || isPhoneValid) &&
    !submission.isSubmitting;
  const isExiting = animationState === 'exit';

  const panelClassName = cn(styles.lockedOverlay__panel, {
    [styles.lockedOverlay__panel_modExit]: isExiting,
  });
  const backdropClassName = cn(styles.lockedOverlay__backdrop, {
    [styles.lockedOverlay__backdrop_modExit]: isExiting,
  });

  const handleAdvisorToggle = (): void => {
    // Reset the phone number when the checkbox is toggled off
    if (talkToAdvisor) {
      setPhone('');
    }

    setTalkToAdvisor((prev) => !prev);
  };

  const handleSubmit = async (
    event: React.SyntheticEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!canSubmit || isSubmittingRef.current) return;

    isSubmittingRef.current = true;
    setSubmission({isSubmitting: true, error: null});

    const trimmedFullName = fullName.trim();
    const firstName = trimmedFullName.split(/\s+/)[0] ?? '';
    const phoneNumber = talkToAdvisor ? cleanPhone : '';

    try {
      await request(LEAD_INGEST_URL, {
        method: 'POST',
        body: {
          email,
          firstName,
          phone: phoneNumber,
          lm_name: fullName,
          lm_payload: {
            ...leadPayload,
            email,
            phone: phoneNumber,
            talk_to_advisor: talkToAdvisor,
            investable_assets: getInvestableAssetsBucket(
              leadPayload.result.investableAssets,
            ),
            submittedAt: new Date().toISOString(),
          },
        },
      });
    } catch (error) {
      logger.error({error}, 'FHS: lead ingest failed');
      isSubmittingRef.current = false;
      setSubmission({isSubmitting: false, error: 'Please try again'});

      return;
    }

    setSubmission({isSubmitting: false, error: null});
    onUnlock();
  };

  return (
    <div className={styles.lockedOverlay} aria-hidden={!isOpen}>
      <div className={backdropClassName} />

      <aside className={panelClassName} aria-label="Unlock your results">
        <form className={styles.lockedOverlay__form} onSubmit={handleSubmit}>
          <Image
            src="/static/img/zoe-logo/Zoe_Logo_Blue.svg"
            alt="Financial Health Score"
            width={100}
            height={40}
            className={styles.lockedOverlay__logo}
          />
          <div className={styles.lockedOverlay__copy}>
            <p className={styles.lockedOverlay__subtitle}>
              Your Financial Health Score is ready.
            </p>
            <h3 className={styles.lockedOverlay__title}>Unlock Your Results</h3>
          </div>

          <div className={styles.lockedOverlay__inputWrapper_email}>
            <label
              htmlFor="fhs-submit-email"
              className={styles.lockedOverlay__emailLabel}>
              Where should we send your Financial Health Score?
            </label>
            <InputString
              id="fhs-submit-email"
              name="email"
              placeholder="you@email.com"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              className={styles.lockedOverlay__input}
            />
          </div>

          <div className={styles.lockedOverlay__advisorCheckboxWrapper}>
            <Checkbox
              id="fhs-submit-talk-to-advisor"
              checked={talkToAdvisor}
              checkHandler={handleAdvisorToggle}
              className={styles.lockedOverlay__advisorCheckbox}
            />
            <label
              htmlFor="fhs-submit-talk-to-advisor"
              className={styles.lockedOverlay__advisorLabel}>
              I would like to speak to an advisor
            </label>
          </div>

          {/*
            Reveal driven by grid-template-rows, not height. Animating `height`
            forces the browser to lay out every frame, and `height: 'auto'`
            additionally makes Motion measure the content each time. A grid
            track interpolating 0fr -> 1fr needs no measurement, and the
            transition is declared in CSS so the element can stay mounted and
            animate in both directions without AnimatePresence.

            `inert` keeps the collapsed input out of the tab order and the
            accessibility tree — it is visually hidden by overflow, so it must
            not be reachable.
          */}
          <div
            className={cn(styles.lockedOverlay__phoneReveal, {
              [styles.lockedOverlay__phoneReveal_open]: talkToAdvisor,
            })}
            inert={!talkToAdvisor}>
            <div className={styles.lockedOverlay__phoneReveal_inner}>
              <div className={styles.lockedOverlay__inputWrapper_phone}>
                <label htmlFor="fhs-submit-phone">
                  Enter your Phone number
                </label>
                <InputMobile
                  id="fhs-submit-phone"
                  name="phone"
                  testId="fhs-submit-phone-input"
                  placeholder="( ___ ) ___-____"
                  value={phone}
                  hasError={!isPhoneValid && cleanPhone.length === 10}
                  errorMessage="Please enter a valid phone number"
                  className={styles.lockedOverlay__input}
                  onChange={(event) => setPhone(event.target.value)}
                />
              </div>
            </div>
          </div>

          <div className={styles.lockedOverlay__submitBlock}>
            <Button
              as="button"
              type="submit"
              size="large"
              disabled={!canSubmit}
              loading={submission.isSubmitting}
              className={styles.lockedOverlay__submit}>
              Show my Score
            </Button>
            {submission.error && (
              <p className={styles.lockedOverlay__error} role="alert">
                {submission.error}
              </p>
            )}
            <p className={styles.lockedOverlay__passiveConsent}>
              I consent to the collection, use, and sharing of my responses as
              described in the{' '}
              <a
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.lockedOverlay__link}>
                Privacy Policy.
              </a>{' '}
              I understand I may withdraw consent, request deletion of my data,
              or opt out of sharing at any time by contacting{' '}
              <a
                href={`mailto:${PRIVACY_EMAIL}`}
                className={styles.lockedOverlay__link}>
                {PRIVACY_EMAIL}
              </a>
            </p>
          </div>
        </form>
      </aside>
    </div>
  );
};

export default LockedOverlay;
